import React, { useState, useEffect, useCallback } from 'react';
import { View, StyleSheet, FlatList, RefreshControl, TouchableOpacity } from 'react-native';
import { Text, Card, ActivityIndicator } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const MisVentasHoyScreen = () => {
  const navigation = useNavigation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [ventas, setVentas] = useState([]);
  const [totales, setTotales] = useState({ total_ventas: 0, total_monto: 0, total_abonos_credito: 0 });
  const [filterCobro, setFilterCobro] = useState('todas'); // todas | cobradas | credito_pendiente

  const formatCurrency = (value) => {
    const numValue = parseFloat(value || 0);
    return `L. ${isNaN(numValue) ? '0.00' : numValue.toFixed(2)}`;
  };

  const cargarVentas = useCallback(async () => {
    if (!user) return;
    try {
      const response = await api.get(`/reportes/venta-diaria?periodo=hoy&vendedor_id=${user.id}&incluir_credito_sin_abono=1`);
      setVentas(response.ventas || []);
      setTotales(response.totales || { total_ventas: 0, total_monto: 0, total_abonos_credito: 0 });
    } catch (error) {
      console.error('Error al cargar mis ventas:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user]);

  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      cargarVentas();
    });
    return unsubscribe;
  }, [navigation, cargarVentas]);

  useEffect(() => { cargarVentas(); }, [cargarVentas]);

  const onRefresh = () => {
    setRefreshing(true);
    cargarVentas();
  };

  const getMetodoPagoLabel = (metodo) => {
    const metodos = { efectivo: 'Efectivo', tarjeta: 'Tarjeta', transferencia: 'Transferencia', credito: 'Crédito' };
    return metodos[metodo] || metodo || 'Efectivo';
  };

  // Una venta tiene crédito pendiente si su CxC aún tiene saldo > 0.
  const tieneCreditoPendiente = (v) =>
    (v.metodo_pago || '').toLowerCase() === 'credito' &&
    parseFloat(v.credito_saldo_pendiente || 0) > 0.01;

  // Lo que NO entró a caja todavía: suma de saldos pendientes de las ventas de hoy.
  // (Si el cliente abonó parte el mismo día, ya está descontado.)
  const totalCreditoPendiente = ventas
    .filter(v => v.estado !== 'cancelada' && tieneCreditoPendiente(v))
    .reduce((sum, v) => sum + parseFloat(v.credito_saldo_pendiente || 0), 0);

  // Lo que entró a caja por ventas de hoy = total facturado - lo que quedó pendiente.
  const totalCobradoDeVentasHoy = (parseFloat(totales.total_monto) || 0) - totalCreditoPendiente;

  // Abonos a CxC recibidos hoy (cobros de crédito antiguo).
  const totalAbonosHoy = parseFloat(totales.total_abonos_credito || 0);
  const numAbonosHoy = parseInt(totales.num_abonos_credito || 0);

  const ventasFiltradas = ventas.filter(v => {
    if (filterCobro === 'cobradas') return !tieneCreditoPendiente(v);
    if (filterCobro === 'credito_pendiente') return tieneCreditoPendiente(v);
    return true;
  });

  const ESTADOS = {
    completada:             { label: 'Completada',      color: '#28a745', textColor: '#fff' },
    pendiente:              { label: 'Pendiente',       color: '#ffc107', textColor: '#000' },
    cancelada:              { label: 'Cancelada',       color: '#dc3545', textColor: '#fff' },
    entregado:              { label: 'Entregado',       color: '#17a2b8', textColor: '#fff' },
    en_ruta:                { label: 'En Ruta',         color: '#6f42c1', textColor: '#fff' },
    preparado:              { label: 'Preparado',       color: '#007bff', textColor: '#fff' },
    devuelto:               { label: 'Devuelto',        color: '#fd7e14', textColor: '#fff' },
    parcialmente_devuelto:  { label: 'Parc. Devuelto',  color: '#fd7e14', textColor: '#fff' },
  };

  const getEstadoInfo = (estado) => ESTADOS[estado] || { label: estado || 'Completada', color: '#6c757d', textColor: '#fff' };

  const renderVenta = ({ item }) => {
    const estadoInfo = getEstadoInfo(item.estado);
    const pendiente = tieneCreditoPendiente(item);
    return (
      <TouchableOpacity onPress={() => navigation.navigate('Ventas', { screen: 'DetalleVenta', params: { venta: { id: item.venta_id } } })}>
        <Card style={styles.ventaCard}>
          <Card.Content>
            <View style={styles.ventaHeader}>
              <View style={{ flex: 1 }}>
                <Text style={styles.ventaCliente}>{item.cliente_nombre || 'Consumidor Final'}</Text>
                <Text style={styles.ventaHora}>{item.hora}</Text>
              </View>
              <Text style={styles.ventaMonto}>{formatCurrency(item.monto_factura)}</Text>
            </View>
            <View style={styles.ventaFooter}>
              <View style={[styles.estadoChip, { backgroundColor: estadoInfo.color }]}>
                <Text style={[styles.estadoChipText, { color: estadoInfo.textColor }]}>{estadoInfo.label.toUpperCase()}</Text>
              </View>
              <Text style={styles.ventaMetodo}>{getMetodoPagoLabel(item.metodo_pago)}</Text>
              {pendiente && (
                <View style={styles.creditoBadge}>
                  <Ionicons name="time" size={10} color="#fff" />
                  <Text style={styles.creditoBadgeText}>Por cobrar {formatCurrency(item.credito_saldo_pendiente)}</Text>
                </View>
              )}
            </View>
            {item.observacion ? <Text style={styles.ventaObs}>{item.observacion}</Text> : null}
          </Card.Content>
        </Card>
      </TouchableOpacity>
    );
  };

  if (loading && !refreshing) {
    return (
      <View style={styles.loader}>
        <ActivityIndicator size="large" color="#0066cc" />
        <Text style={{ marginTop: 10, color: '#666' }}>Cargando ventas...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Resumen */}
      <View style={styles.resumenContainer}>
        <Card style={styles.resumenCard}>
          <Card.Content style={styles.resumenContent}>
            <Ionicons name="receipt-outline" size={22} color="#0066cc" />
            <Text style={styles.resumenValue}>{totales.total_ventas}</Text>
            <Text style={styles.resumenLabel}>Ventas</Text>
          </Card.Content>
        </Card>
        <Card style={styles.resumenCard}>
          <Card.Content style={styles.resumenContent}>
            <Ionicons name="cash-outline" size={22} color="#28a745" />
            <Text style={[styles.resumenValue, { color: '#28a745' }]}>{formatCurrency(totalCobradoDeVentasHoy)}</Text>
            <Text style={styles.resumenLabel}>Cobrado</Text>
          </Card.Content>
        </Card>
        <Card style={styles.resumenCard}>
          <Card.Content style={styles.resumenContent}>
            <Ionicons name="time-outline" size={22} color="#ff9800" />
            <Text style={[styles.resumenValue, { color: '#ff9800' }]}>{formatCurrency(totalCreditoPendiente)}</Text>
            <Text style={styles.resumenLabel}>Por cobrar</Text>
          </Card.Content>
        </Card>
      </View>
      <Text style={styles.resumenAyuda}>
        Facturado hoy: {formatCurrency(totales.total_monto)}
        {numAbonosHoy > 0 ? `  ·  Abonos a CxC recibidos: ${formatCurrency(totalAbonosHoy)} (${numAbonosHoy})` : ''}
      </Text>

      {/* Filtros de cobro */}
      <View style={styles.cobroFiltersRow}>
        {[
          { key: 'todas', label: 'Todas', color: '#007bff' },
          { key: 'cobradas', label: 'Cobradas', color: '#28a745' },
          { key: 'credito_pendiente', label: 'Crédito pend.', color: '#e67e22' },
        ].map(f => {
          const isSelected = filterCobro === f.key;
          const count = f.key === 'todas'
            ? ventas.length
            : f.key === 'credito_pendiente'
              ? ventas.filter(tieneCreditoPendiente).length
              : ventas.filter(v => !tieneCreditoPendiente(v)).length;
          return (
            <TouchableOpacity
              key={f.key}
              onPress={() => setFilterCobro(f.key)}
              style={[
                styles.cobroFilterChip,
                isSelected && { backgroundColor: f.color, borderColor: f.color }
              ]}
            >
              <Text style={[styles.cobroFilterChipText, isSelected && { color: '#fff' }]}>
                {f.label} ({count})
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Lista */}
      <FlatList
        data={ventasFiltradas}
        keyExtractor={(item, idx) => `${item.venta_id}-${idx}`}
        renderItem={renderVenta}
        contentContainerStyle={styles.listContent}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        ListEmptyComponent={
          <View style={styles.empty}>
            <Ionicons name="bag-outline" size={60} color="#ccc" />
            <Text style={styles.emptyText}>No has realizado ventas hoy</Text>
            <Text style={styles.emptySubtext}>Tus ventas del día aparecerán aquí</Text>
          </View>
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  loader: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  resumenContainer: {
    flexDirection: 'row',
    padding: 12,
    paddingBottom: 4,
  },
  resumenCard: {
    flex: 1,
    marginHorizontal: 4,
    elevation: 2,
  },
  resumenContent: {
    alignItems: 'center',
    paddingVertical: 12,
  },
  resumenValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0066cc',
    marginTop: 4,
  },
  resumenLabel: {
    fontSize: 12,
    color: '#888',
    marginTop: 2,
  },
  resumenAyuda: {
    textAlign: 'center',
    fontSize: 11,
    color: '#999',
    fontStyle: 'italic',
    paddingHorizontal: 12,
    paddingBottom: 4,
  },
  listContent: {
    padding: 12,
    paddingTop: 8,
  },
  ventaCard: {
    marginBottom: 8,
    elevation: 2,
    borderRadius: 8,
  },
  ventaHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  ventaCliente: {
    fontSize: 15,
    fontWeight: '600',
    color: '#333',
  },
  ventaHora: {
    fontSize: 13,
    color: '#888',
    marginTop: 2,
  },
  ventaMonto: {
    fontSize: 16,
    fontWeight: '700',
    color: '#0066cc',
  },
  ventaFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  estadoChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
    marginRight: 8,
  },
  estadoChipText: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  ventaMetodo: {
    fontSize: 12,
    color: '#888',
  },
  creditoBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#e67e22',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
    marginLeft: 8,
    gap: 3,
  },
  creditoBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#fff',
  },
  cobroFiltersRow: {
    flexDirection: 'row',
    paddingHorizontal: 12,
    marginBottom: 4,
    gap: 6,
  },
  cobroFilterChip: {
    flex: 1,
    paddingVertical: 6,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#fff',
    alignItems: 'center',
  },
  cobroFilterChipText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#666',
  },
  ventaObs: {
    fontSize: 12,
    color: '#999',
    fontStyle: 'italic',
    marginTop: 6,
  },
  empty: {
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyText: {
    fontSize: 16,
    color: '#888',
    marginTop: 12,
    fontWeight: '600',
  },
  emptySubtext: {
    fontSize: 13,
    color: '#aaa',
    marginTop: 4,
  },
});

export default MisVentasHoyScreen;
